import React, { useMemo } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  normalizeAssigneeSelectionOnChange,
  selectionAllowsMultiple,
  assigneeSelectionLabels,
} from "../utils/assigneeTransferField";
import {
  buildSkillLifecycleTransferOptions,
  lifecycleTransferAllowsLoggedUsersOnly,
  lifecycleTransferToSelection,
  selectionToLifecycleTransfer,
} from "../utils/skillLifecycleTransfer";
import LoggedUsersFallbackField from "./LoggedUsersFallbackField";
import { applyLoggedUsersOnlyFlag } from "../utils/loggedUsersFallback";

/**
 * Select unificado de transferência nas regras de ciclo de vida das skills de agentes de IA.
 */
export default function SkillLifecycleTransferField({
  label,
  transfer = {},
  users = [],
  agents = [],
  excludeAgentIds = [],
  shortcutLabels = {},
  loading = false,
  disabled = false,
  helperText,
  onChange,
  showLoggedUsersOnly = true,
}) {
  const theme = useTheme();
  const { t } = useTranslation("settings");

  const options = useMemo(
    () =>
      buildSkillLifecycleTransferOptions({
        users,
        agents,
        excludeAgentIds,
        labels: shortcutLabels,
      }),
    [users, agents, excludeAgentIds, shortcutLabels]
  );

  const selected = useMemo(
    () =>
      lifecycleTransferToSelection(transfer, {
        users,
        agents,
        labels: shortcutLabels,
      }),
    [transfer, users, agents, shortcutLabels]
  );

  const allowMultiple = selectionAllowsMultiple(selected);
  const allowLoggedUsersOnly = lifecycleTransferAllowsLoggedUsersOnly(transfer);
  const loggedUsersOnly = transfer?.logged_users_only === true;

  const handleChange = (_, nextRaw) => {
    const nextArr = allowMultiple
      ? Array.isArray(nextRaw)
        ? nextRaw
        : nextRaw
        ? [nextRaw]
        : []
      : nextRaw
      ? [nextRaw]
      : [];
    const normalized = normalizeAssigneeSelectionOnChange(selected, nextArr);
    // Não reenviar logged_users_only aqui: o checkbox é a única fonte intencional do flag.
    // Se o Autocomplete re-disparar com closure stale, sobrescreveria o valor marcado.
    const nextTransfer = selectionToLifecycleTransfer(normalized, {
      tipo_id: transfer?.tipo_id || "",
      tipo1_id: transfer?.tipo1_id || "",
      tipo2_id: transfer?.tipo2_id || "",
      tipo3_id: transfer?.tipo3_id || "",
    });
    const { logged_users_only: _omitFlag, ...transferWithoutFlag } = nextTransfer;
    onChange?.(transferWithoutFlag, assigneeSelectionLabels(normalized));
  };

  return (
    <Box>
      <Autocomplete
        multiple={allowMultiple}
        disabled={disabled}
        options={options}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(a, b) =>
          a?.kind === b?.kind && String(a?.id ?? "") === String(b?.id ?? "")
        }
        value={allowMultiple ? selected : selected[0] ?? null}
        onChange={handleChange}
        loading={loading}
        filterSelectedOptions
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            margin="dense"
            label={label}
            helperText={
              helperText ||
              "Vazio = não transferir. Usuários: vários. Agente, ChatBot, fila ou recepcionista: apenas um."
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            key={`${option.kind}-${option.id}`}
            style={{
              ...props.style,
              fontWeight:
                option.kind === "agent" || option.kind === "receptionist"
                  ? 600
                  : option.kind === "chatbot"
                  ? 500
                  : option.kind === "queue"
                  ? 500
                  : 400,
              color:
                option.kind === "agent" || option.kind === "receptionist"
                  ? theme.palette.secondary.main
                  : option.kind === "chatbot"
                  ? theme.palette.warning?.main || "#ed6c02"
                  : option.kind === "queue"
                  ? theme.palette.text.secondary
                  : undefined,
            }}
          >
            {option.label}
          </li>
        )}
      />
      {showLoggedUsersOnly ? (
        <FormControlLabel
          sx={{ mt: 0.5, ml: 0, alignItems: "flex-start" }}
          control={
            <Checkbox
              size="small"
              checked={allowLoggedUsersOnly && loggedUsersOnly}
              disabled={disabled || !allowLoggedUsersOnly}
              onChange={(e) => {
                if (!allowLoggedUsersOnly) return;
                onChange?.(
                  applyLoggedUsersOnlyFlag(
                    {
                      ...transfer,
                      dest: transfer?.dest || "humano",
                      target_id: transfer?.target_id || "",
                    },
                    e.target.checked
                  ),
                  assigneeSelectionLabels(selected)
                );
              }}
            />
          }
          label={
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="body2">
                {t("agentsListPage.lifecycleHandoffLoggedUsersOnly", {
                  defaultValue: t("agentsListPage.recoveryHandoffLoggedUsersOnly"),
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {t("agentsListPage.lifecycleHandoffLoggedUsersOnlyHelper", {
                  defaultValue: t("agentsListPage.recoveryHandoffLoggedUsersOnlyHelper"),
                })}
              </Typography>
            </Box>
          }
        />
      ) : null}
      {showLoggedUsersOnly && allowLoggedUsersOnly && loggedUsersOnly ? (
        <LoggedUsersFallbackField
          fallback={transfer?.logged_users_fallback}
          users={users}
          agents={agents}
          disabled={disabled}
          onChange={(nextFallback) =>
            onChange?.(
              {
                ...transfer,
                logged_users_only: true,
                logged_users_fallback: nextFallback,
              },
              assigneeSelectionLabels(selected)
            )
          }
        />
      ) : null}
    </Box>
  );
}
